/* eslint-disable max-lines */
import {
  Component, ChangeDetectionStrategy, ViewChild, ElementRef, AfterViewInit,
  ChangeDetectorRef, Inject, Renderer2, Output, EventEmitter, Input, OnInit, OnDestroy
} from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { FlightType } from '@search-bar/entities/flight-type';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { CalendarField } from '@shared/base-search-bar/entities/calendar.entities';
import { PlaceType } from '@shared/base-search-bar/entities/place-type';
import { FlightClass } from '@shared/base-search-bar/entities/flight-class';
import { FLIGHT_PASSENGER_MAX, PassengerArrangement } from '@shared/base-search-bar/entities/passenger-arrangement';
import { SearchType } from '@search-bar/entities/search-type';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { SearchBarMobileService } from '@search-bar/services/search-bar-mobile.service';
import { blockSearchByDates, canSearch, blockSearchByCountry } from '@ux/mappers/date.mapper';
import { RappiTravelModules } from '@ux/entities/modules';
import { SearchData } from '@shared/base-search-bar/entities/search-data';
import { mapSearchData } from '@search-bar/mappers/url-search.mapper';
import { TripTypes } from '@app/entities/trip-types';
import { SearchBarAbstractComponent } from '@search-bar/abstracts/search-bar-abstract.component';
import { HomeFlightOffers } from '@shared/home-sections/entities/home-data.response';
import { PropertiesAmplitudeService } from '@app/services/properties-amplitude.service';
import { AvoService } from '@avo/services/avo.service';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { AvoDispatchOrchestrator } from '@avo/orchestrator/avo.dispatch';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import {
  TypographyColor,
  TypographyFontFamily,
  TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';
import { environment as ENV } from '@environment';
import { Countries } from '@ux/entities/country.enum';
import { RadioButtonAlign } from '@design-system/components/organisms/radio-button/entities/radio-button.types';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { calculatePassengerArrangement } from '@app/modules/shared/base-search-bar/helpers/search-engine.helper';
import { SearchBy } from '@shared/base-search-bar/entities/search.entities';

@Component({
  selector: 'app-search-bar-flights-mobile',
  templateUrl: './search-bar-flights-mobile.component.html',
  styleUrls: ['./search-bar-flights-mobile.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})

export class SearchBarFlightsMobileComponent extends SearchBarAbstractComponent implements AfterViewInit, OnInit, OnDestroy {

  @Input() baggageIncluded: boolean;
  @Input() isResultsPage: boolean;
  @Input() currentUrl: string;
  @Input() searchData: SearchData;
  @Input() isExchangeSearch: boolean;
  @Input() departureDateIsDisabled: boolean;
  @Input() flightOffers: HomeFlightOffers[] = [];
  @Input() source: EVENT_SOURCE | string;
  @Input()
  set validate(value: boolean) {
    if (value) {
      this.searchFlights();
    }
  }
  @Output() selectedBaggageIncluded: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() showMobileCalendarEvent: EventEmitter<CalendarField> = new EventEmitter<CalendarField>();
  @Output() toggleSearchByFlexDates: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() toggleEvent: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('flightOptions', { static: false }) public flightOptions: ElementRef;

  public eventPath: BehaviorSubject<EventTarget[]> = new BehaviorSubject<EventTarget[]>([]);
  public showFlightOptionSelection: boolean = false;
  public activeOption: string;
  public searching: boolean = false;
  public showCalendarInput: boolean = false;
  public activeCalendarType: CalendarField = CalendarField.departure;
  public departure: PlaceType = PlaceType.DEPARTURE;
  public arrival: PlaceType = PlaceType.ARRIVAL;
  public flightTypes: FlightType[] = [
    FlightType.ROUNDTRIP, FlightType.ONEWAY
  ];
  public flightClassTypes: FlightClass[] = [
    FlightClass.CHEAPER, FlightClass.PREMIUM,
    FlightClass.EXECUTIVE, FlightClass.FIRST_CLASS
  ];
  public isFlexDateCheck = false;
  public typographyFontSize = TypographyFontSize;
  public typographyColor = TypographyColor;
  public typographyFontFamily = TypographyFontFamily;

  public maxPassengers: number = FLIGHT_PASSENGER_MAX;
  public totalPassengers: number = 0;
  public searchBy = SearchBy.FLIGHTS

  private moveY: number = 0;
  private disabledPlaceSelection: boolean = false;
  private readonly subscriptionFlexDate: Subscription;
  protected AvoDispatch = new AvoDispatchOrchestrator();

  public readonly radioButtonAlign = RadioButtonAlign.Right;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly renderer: Renderer2,
    private readonly domManipuleService: DomManipulateService,
    private readonly searchBarMobileService: SearchBarMobileService,
    protected amplitude: PropertiesAmplitudeService,
    protected avoService: AvoService,
    protected router: Router,
    @Inject(IS_BROWSER) public isBrowser: boolean
  ) {
    super();
  }

  ngOnInit(): void {
    this.domManipuleService.setBodyScroll(false);
  }

  ngAfterViewInit(): void {
    if (this.isBrowser && this.flightOptions) {
      this.renderer.listen(this.flightOptions.nativeElement, 'touchstart', (event) => this.onTouchStart(event));
      this.renderer.listen(this.flightOptions.nativeElement, 'touchend', (event) => this.onTouchEnd(event));
    }
  }

  ngOnDestroy(): void {
    this.domManipuleService.setBodyScroll(true);
    this.subscriptionFlexDate?.unsubscribe();
  }

  get disabledPlace(): boolean {
    return (this.disabledPlaceSelection && this.departureDateIsDisabled) || this.routeChangeIsDisabled;
  }

  get showArrivalInput(): boolean {
    return this.exchangeTripType ?
      ((this.isOneWayTrip && this.exchangeTripType === TripTypes.RETURN) || !this.isOneWayTrip) : !this.isOneWayTrip;
  }

  get showDepartureInput(): boolean {
    return this.exchangeTripType ? this.exchangeTripType === TripTypes.DEPARTURE : true;
  }

  get arrowIcon(): Icon {
    return this.searchBarMobileService.arrowIcon(this.isOneWayTrip, true);
  }

  get isEmptyDepartureDate(): boolean {
    return this.searchBarMobileService.isEmptyDepartureDate(this.departureDate, this.isSubmitted);
  }

  get isEmptyArrivalDate(): boolean {
    return this.searchBarMobileService.isEmptyArrivalDate(this.arrivalDate, this.isSubmitted);
  }

  get isDisabled(): boolean {
    return this.disabledPlace || this.destination;
  }

  get isAlert(): boolean {
    return this.searchType === SearchType.ALERT;
  }

  get hasFlightsOffers(): boolean {
    return Array.isArray(this.flightOffers) && this.flightOffers.length > 0 && !this.isExchangeSearch
      && this.searchType !== SearchType.ALERT;
  }

  get showBaggageToggle(): boolean {
    return !this.isAlert && !this.isExchangeSearch;
  }

  get isBR(): boolean {
    return ENV.countryInfo.iso_code === Countries.Brazil; 
  }

  get isValidFlexDates(): boolean {
    return !this.isExchangeSearch && !this.isBR;
  }

  public toggleFlex(value: boolean): void {
    this.isFlexDateCheck = value;
  }

  public swapLocations(): void {
    if (!this.disabledPlace && !this.destination) this.swapLocationsEmitter.emit();
  }

  public toggleFlightOption(option?: string): void {
    this.showFlightOptionSelection = !this.showFlightOptionSelection;
    if (option) this.activeOption = option;
  }

  public showMobileCalendar(type: CalendarField): void {
    this.activeCalendarType = type;
    this.showMobileCalendarEvent.emit(type);
  }

  public flightTypeSelected(type: FlightType): void {
    this.selectFlightType.emit(type);
  }

  public searchFlights(): void {
    const urlParams = mapSearchData(this.searchData);
    const isCurrentUrl = this.currentUrl.includes(urlParams);
    this.isSubmitted = true;
    if (!this.disabledButton) {
      if (!isCurrentUrl && (canSearch(RappiTravelModules.flighs) || (!canSearch(RappiTravelModules.flighs) &&
        (!blockSearchByDates(this.arrivalPlace, this.departureDate) &&
          !blockSearchByCountry(this.arrivalPlace, this.departurePlace, this.departureDate))))) {
        this.searching = true;
      }
      if (!this.isAlert) {
        this.toggleSearchByFlexDates.emit(this.isFlexDateCheck);
        this.emitSearch.emit();
      }
      if (this.isExchangeSearch) {
        const allSegments = this.showArrivalInput && this.showDepartureInput;
        this.amplitudeEvent.emit({ type: AVO_EVENTS.SELECT_CHANGES_SEARCH, props: { ALL_SEGMENTS: allSegments } });
      }
    }
  }

  public baggageIncludedChange(value: boolean): void {
    this.selectedBaggageIncluded.emit(!value);
  }

  private onTouchStart(event: TouchEvent): void {
    if (event.touches) {
      this.moveY = event.touches[0].clientY;
    }
  }
  private onTouchEnd(event: TouchEvent): void {
    const haveMove = this.moveY !== event.changedTouches[0].clientY;
    if (event.changedTouches && haveMove) {
      const moveDirection = this.moveY < event.changedTouches[0].clientY;
      if (moveDirection) {
        this.toggleFlightOption();
        this.changeDetector.detectChanges();
      }
      this.moveY = 0;
    }
  }
  public selectFlightClass(flightClass: FlightClass): void {
    this.emitFlightClassSelection.emit(flightClass);
  }
  public isActiveClass(flightClass: FlightClass): boolean {
    return flightClass === this.activeFlightClass;
  }
  
  public updatePassengerArrangement(quantity: number, type: PassengerArrangement): void {
    const { updatedArrangement, adjustedQuantity } = calculatePassengerArrangement(
      quantity,
      type,
      this.passengerArrangement,
      this.maxPassengers
    );
    this.passengerArrangement = updatedArrangement;
    this.emitArrangement.emit({ ...type, quantity: adjustedQuantity });
    this.totalPassengers = this.passengerArrangement.reduce((sum, p) => sum + p.quantity, 0);
  }

}
